import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { groupingBasisOf, groupWords, formatGroupName } from 'utils/groupLabel';
import { useDispatch, useSelector } from 'react-redux';
import MetaTags from 'components/common/MetaTags';
import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import AdminPageHeader from 'components/admin/AdminPageHeader';
import AdminPageOption from 'components/admin/AdminPageOption';
import AdminDataTable from 'components/admin/AdminDataTable';
import CustomNewButton from 'components/NewDesign/CustomNewButton/CustomNewButton';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import {
  getPublishColumns,
  publishColumns,
  pendingColumns,
  dataTypeOptions,
} from 'config/admin/clubRecord';
import { getUserPermissionLevel } from 'services/auth/tokenService';
import { UserPermissionLevel } from 'constants/auth';
import { clubFilterOptions } from 'config/admin/filterOptions';
import useSessionFilters from 'hooks/useSessionFilters';
import styles from './ClubRecords.module.css';
import filterStyles from 'components/admin/bulkAdminTable.module.css';
import { redColor } from 'config/global';
import ClubRecordApprovalPopup from 'components/admin/ClubRecordApprovalPopup';
import ClubRecordEditPopup from 'components/admin/ClubRecordEditPopup';
import {
  getAllClubRecords,
  getPendingClubRecords,
  getPendingClubRecordsCount,
  updateStatusOfPendingClubRecordResult,
  addClubRecord,
  editClubRecord,
  deleteClubRecord,
  updatePendingClubRecords,
} from 'api/clubApi';
import { getClubEventTypes } from 'api/club/eventTypeApi';
import { getClubAgeGroups } from 'api/club/ageGroupApi';
import { clubRecordEventOptions } from 'utils/eventTypeOptions';
import { clubRecordAgeOptions } from 'utils/clubRecordAges';
import {
  getOfficialRecordTableSortData,
  getPendingRecordTableSortData,
} from 'services/admin/ClubRecordService';
import { toast } from 'react-toastify';
import IconTextButton from 'components/common/IconTextButton';
import { ReactComponent as EyeIcon } from 'assets/images/icon_eye.svg';
import { updateClubRecordsPendingCount } from 'services/user/userSlice';
import { convertMillisecondsToRecord } from 'utils/time';
import { organisationTitleOf, organisationWordOf } from 'utils/clubFeatures';
import { ReactComponent as InfoIcon } from 'assets/images/icon_info_circle.svg';

// A filter's choices come from the rows on show, so nobody can pick a value
// with nothing behind it. Events in the program order of the website's list
// (config/admin/filterOptions), anything it does not know last, by name.
const eventsInListOrder = (rows) => {
  const present = new Set(rows.map((record) => record.eventType));
  const listOrder = clubFilterOptions.eventType.values;
  const known = listOrder.filter((event) => present.has(event));
  const extras = [...present]
    .filter((event) => !listOrder.includes(event))
    .sort();
  return [...known, ...extras];
};

// Groups in number order, any named group after them.
const groupsInOrder = (rows) => {
  const ages = [...new Set(rows.map((record) => String(record.ageGroup)))];
  return ages.sort((a, b) => {
    const aNum = parseInt(a);
    const bNum = parseInt(b);
    if (isNaN(aNum) && isNaN(bNum)) return a.localeCompare(b);
    if (isNaN(aNum)) return 1;
    if (isNaN(bNum)) return -1;
    return aNum - bNum;
  });
};

const gendersInOrder = (rows) => {
  const present = new Set(rows.map((record) => record.gender).filter(Boolean));
  const listOrder = clubFilterOptions.gender.values;
  const known = listOrder.filter((gender) => present.has(gender));
  const extras = [...present].filter((gender) => !listOrder.includes(gender)).sort();
  return [...known, ...extras];
};

// The message after a club record is deleted. The server marks the best
// remaining result for the record's event, group and gender pending; nothing
// replaces the record until that result is approved (Doug agreed the words,
// 2026-10-16). organisationTitle: "Club", or "School" for a school.
const deletedRecordMessage = (data, organisationTitle) =>
  data?.newPendingResultId
    ? 'Record deleted. The best remaining result for this event is now under Pending Records, waiting for review.'
    : `${organisationTitle} record deleted.`;

const ClubRecords = () => {
  const dispatch = useDispatch();
  const { selectedClub } = useSelector((state) => state.user || {});
  const { clubRecordsPendingCount } = useSelector((state) => state.user || 0);
  const clubId = selectedClub?.clubID || '';
  // The group a record is for reads as a number under "Age Group" for an age club, and as the year (Y5) under
  // "Year Group" for a club that groups by year level. See utils/groupLabel.js.
  const groupingBasis = groupingBasisOf(selectedClub);
  const words = groupWords(groupingBasis);
  // The page is Club Records, or School Records for a school (Doug, 2026-10-26), and its messages and
  // dialogs say "club record" or "school record" (pass 2, 2026-10-27).
  const organisationWord = organisationWordOf(selectedClub);
  const organisationTitle = organisationTitleOf(selectedClub);
  const recordsTitle = `${organisationTitle} Records`;
  const userPermissionLevel = getUserPermissionLevel();
  // Members get a read-only view: Official Records only, no pending tab,
  // no add/edit/delete actions.
  const isOfficial = userPermissionLevel >= UserPermissionLevel.Official;
  // Update Pending Records is for Admins and Owners, as the server allows.
  const isAdmin = userPermissionLevel >= UserPermissionLevel.Admin;
  const [isUpdatingPending, setIsUpdatingPending] = useState(false);
  const [publishedRecords, setPublishedRecords] = useState([]);
  const [pendingRecords, setPendingRecords] = useState([]);
  const [fetchingPublishedRecords, setFetchingPublishedRecords] =
    useState(true);
  const [fetchingPendingRecords, setFetchingPendingRecords] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [selectedClubRecordResult, setSelectedClubRecordResult] = useState({});
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Which list is showing, Pending Records or Official Records, kept for the
  // browser tab like the filters below, so coming back to the page shows the
  // list that was left (Doug, 2026-10-23: navigating away lost every
  // choice). A member only ever sees Official Records.
  const [storedView, setStoredView] = useSessionFilters(`clubRecordsView_${clubId}`, {
    dataType: isOfficial ? 'pending' : 'published',
  });
  const optionValues = useMemo(
    () => ({ dataType: isOfficial ? storedView.dataType : 'published' }),
    [isOfficial, storedView.dataType]
  );

  // Toolbar filters — persisted in sessionStorage so they survive
  // navigate-away-and-back (same pattern as ClubMembers/reports pages).
  // The Official view starts on one event; All Events lists every record
  // (Doug, 2026-10-23: "I think we need to change it to allow all records").
  // Every record is loaded already, so All Events asks for nothing more.
  const [filters, setFilter] = useSessionFilters(`clubRecordsFilters_${clubId}`, {
    searchText: '',
    eventType: '100m',
    ageGroup: 'all',
    gender: 'all',
  });
  // Athlete search deliberately ignores the dropdowns (it answers "show me
  // this athlete's records", not "browse this event") — while active, the
  // dropdowns are disabled so no filter appears applied when it isn't.
  const searchActive = filters.searchText.trim().length > 0;
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showClubRecordEditPopup, setShowClubRecordEditPopup] = useState(false);
  const [clubRecordEditMode, setClubRecordEditMode] = useState('edit'); // 'edit' or 'add'
  // The Add Record dialog's events and ages, for the club they were asked for.
  const [recordEventOptions, setRecordEventOptions] = useState({
    clubId: null,
    options: [],
    ageOptions: [],
  });
  // The Pending Records tab's own filters (step 3 file, 11.6, item 8), apart
  // from the Records tab's, so a choice made there never hides a result
  // waiting for review. Kept for the browser tab like the others (Doug,
  // 2026-10-23); a choice whose results have all been reviewed acts as All.
  const [pendingFilters, setPendingFilter] = useSessionFilters(
    `clubRecordsPendingFilters_${clubId}`,
    {
      eventType: 'all',
      ageGroup: 'all',
      gender: 'all',
    }
  );

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleOptionChange = (name, value) => {
    setStoredView(name, value);
  };

  // Handle edit club record
  const handleEditRecord = (record) => {
    setSelectedRecord(record);
    setClubRecordEditMode('edit');
    setShowClubRecordEditPopup(true);
  };

  // Handle delete club record from table
  const handleDeleteRecord = (record) => {
    setRecordToDelete(record);
    setShowDeleteConfirm(true);
  };

  // Confirm delete from table
  const confirmDeleteRecord = async () => {
    if (!recordToDelete) return;

    setIsDeleting(true);
    try {
      const response = await deleteClubRecord(recordToDelete.recordId);
      toast.success(deletedRecordMessage(response.data, organisationTitle));

      // Refresh all data
      await refreshAllData();
      setShowDeleteConfirm(false);
      setRecordToDelete(null);
    } catch (error) {
      console.error('Error deleting club record:', error);
      const errorMessage =
        error?.response?.data || error?.message || 'Failed to delete record';
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  // Helper function to determine if event is a time-based event
  const isTimeEvent = (eventType) => {
    const fieldEvents = [
      'High Jump',
      'Pole Vault',
      'Long Jump',
      'Triple Jump',
      'Shot Put',
      'Discus',
      'Hammer',
      'Javelin',
      'Vortex',
    ];
    return eventType && !fieldEvents.includes(eventType);
  };

  // Helper function to format record value for display
  const formatRecordValueForDisplay = (rawValue, eventType) => {
    if (!rawValue || !eventType) return '';

    if (isTimeEvent(eventType)) {
      // Time event - convert milliseconds to display format
      return convertMillisecondsToRecord(parseFloat(rawValue), 2);
    } else {
      // Field event - already in meters, just show as decimal
      return rawValue.toString();
    }
  };

  // Handle add new club record. The dialog's events are the club's own event
  // types, and its ages the club's own age groups, one age at a time (Doug,
  // 2026-10-23: "The extra request is fine"): both asked for when + Add Record
  // is first clicked and kept while the page is open (Doug, 2026-10-16:
  // nothing is added to opening the page). Edit shows the record's event and
  // age as text, so it needs neither.
  const handleAddRecord = async () => {
    if (recordEventOptions.clubId !== clubId) {
      try {
        const [eventTypes, ageGroups] = await Promise.all([
          getClubEventTypes(clubId, false),
          getClubAgeGroups(clubId),
        ]);
        setRecordEventOptions({
          clubId,
          options: clubRecordEventOptions(eventTypes.data),
          ageOptions: clubRecordAgeOptions(ageGroups.data, groupingBasis),
        });
      } catch (error) {
        console.error('Error fetching event types or age groups:', error);
        toast.error("The club's events or age groups could not be loaded. Please try again.");
        return;
      }
    }
    setSelectedRecord(null);
    setClubRecordEditMode('add');
    setShowClubRecordEditPopup(true);
  };

  // Handle save club record (add or edit)
  const handleSaveClubRecord = async (recordData) => {
    try {
      if (clubRecordEditMode === 'add') {
        const response = await addClubRecord({
          clubId: selectedClub.clubID,
          ...recordData,
        });
        toast.success(`${organisationTitle} record added successfully`);

        // Show info about cleared pending records if any
        if (response.data?.pendingRecordsCleared > 0) {
          toast.info(
            `${response.data.pendingRecordsCleared} pending records were cleared as they don't beat this new record`
          );
        }
      } else {
        const response = await editClubRecord(
          selectedRecord.recordId,
          recordData
        );
        toast.success(`${organisationTitle} record updated successfully`);

        // Show info about affected pending records if any
        if (response.data?.pendingRecordsAffected > 0) {
          toast.info(
            `${response.data.pendingRecordsAffected} pending records were affected by this update`
          );
        }

        // Show info about new pending record that might replace this if any
        if (response.data?.newPendingResultId) {
          toast.info('A pending record now beats this updated record');
        }
      }

      // Refresh all data. The filters stay as they were: switching the view to
      // the saved record's event was disorienting (Doug, 2026-10-23: "It
      // should not change the filters after a save").
      await refreshAllData();

      setShowClubRecordEditPopup(false);
    } catch (error) {
      console.error('Error saving club record:', error);
      throw error; // Re-throw to let popup handle the error
    }
  };

  // Handle delete club record from edit popup
  const handleDeleteClubRecord = async (recordId) => {
    try {
      const response = await deleteClubRecord(recordId);
      toast.success(deletedRecordMessage(response.data, organisationTitle));

      // Refresh all data
      await refreshAllData();
      setShowClubRecordEditPopup(false);
    } catch (error) {
      console.error('Error deleting club record:', error);
      throw error; // Re-throw to let popup handle the error
    }
  };

  // Refresh all data after changes
  const refreshAllData = async () => {
    setFetchingPublishedRecords(true);
    setFetchingPendingRecords(true);

    try {
      // Fetch published records
      const publishedResponse = await getAllClubRecords(selectedClub?.clubID);
      if (publishedResponse.data) {
        setPublishedRecords(publishedResponse.data);
      }

      // Fetch pending records
      const pendingResponse = await getPendingClubRecords(selectedClub?.clubID);
      if (pendingResponse.data) {
        setPendingRecords(pendingResponse.data);
      }

      // Update pending count
      const countResponse = await getPendingClubRecordsCount(
        selectedClub?.clubID
      );
      if (countResponse.data) {
        dispatch(updateClubRecordsPendingCount(countResponse.data.count));
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setFetchingPublishedRecords(false);
      setFetchingPendingRecords(false);
    }
  };

  // Update Pending Records (docs/club-records-redesign.md in the portal repo,
  // 4.3; named by Doug, 2026-10-24): checks the current season's results
  // against the club records now and brings Pending Records up to date (the
  // season replay does it within an hour of a change), then the list and the
  // badge's count are asked for again. Shaped like Recalculate Points on the
  // Athlete Points report.
  const handleUpdatePendingRecords = async () => {
    if (!clubId) return;

    setIsUpdatingPending(true);
    toast.info('Updating pending records. This may take a moment...', {
      autoClose: false,
      toastId: 'updatingPendingRecords',
    });

    try {
      await updatePendingClubRecords(clubId);
      toast.dismiss('updatingPendingRecords');
      // No counts (Doug, 2026-10-24: they mean nothing to an admin): the
      // list below shows what changed.
      toast.success('Pending records are up to date.', { autoClose: 6000 });
      await refreshAllData();
    } catch (error) {
      toast.dismiss('updatingPendingRecords');
      console.error('[ClubRecords] Update Pending Records error: ', error);
      toast.error('Failed to update pending records');
    } finally {
      setIsUpdatingPending(false);
    }
  };

  const fetchAllClubRecords = useCallback(async () => {
    try {
      if (selectedClub?.clubID && optionValues.dataType === 'published') {
        setFetchingPublishedRecords(true);
        const response = await getAllClubRecords(selectedClub?.clubID);
        if (response.data) {
          setPublishedRecords(response.data);
        }
      }
    } catch (err) {
      console.log('[ClubRecords] Fetch Published Records error: ', err);
    }
  }, [selectedClub?.clubID, optionValues]);

  const fetchPendingClubRecords = useCallback(async () => {
    try {
      if (selectedClub?.clubID && optionValues.dataType === 'pending') {
        setFetchingPendingRecords(true);
        const response = await getPendingClubRecords(selectedClub?.clubID);
        if (response.data) {
          setPendingRecords(response.data);
        }
      }
    } catch (err) {
      console.log('[ClubRecords] Fetch Pending Records error: ', err);
    }
  }, [selectedClub?.clubID, optionValues]);

  const changeStatusOfPendingClubRecordResult = async (
    status,
    officialName = null
  ) => {
    setShowEditPopup(false);
    try {
      if (selectedClub) {
        setUpdatingStatus(true);
        await updateStatusOfPendingClubRecordResult(
          selectedClub.clubID,
          selectedClubRecordResult.resultId,
          status,
          officialName
        );
        if (status === 'Approved') {
          toast.success(
            `${organisationTitle} record of ${selectedClubRecordResult.result} is Approved`
          );
        } else {
          toast.success(
            `${organisationTitle} record of ${selectedClubRecordResult.result} was rejected`
          );
        }
        setUpdatingStatus(false);
        setFetchingPublishedRecords(true);
        const response = await getAllClubRecords(selectedClub?.clubID);
        if (response.data) {
          setPublishedRecords(response.data);
        }
        setFetchingPublishedRecords(false);
        setFetchingPendingRecords(true);
        const response2 = await getPendingClubRecords(selectedClub?.clubID);
        if (response2.data) {
          setPendingRecords(response2.data);
        }
        setFetchingPendingRecords(false);
        const response3 = await getPendingClubRecordsCount(
          selectedClub?.clubID
        );
        if (response3.data) {
          dispatch(updateClubRecordsPendingCount(response3.data.count));
        }
      }
    } catch (err) {
      console.log(
        '[ClubRecords] Update Status of Pending Club Records Result error: ',
        err
      );
      setUpdatingStatus(false);
      // A refused Approve or Reject means the list on screen was out of date.
      // The server has put the event's marks right where it could and cleared
      // its saved copy, so the list is asked for again before the popup shows
      // the server's message ("The list now shows the current pending
      // records."): no refresh for the admin to make.
      await refreshAllData();
      // Re-throw the error so the popup can handle it
      throw err;
    }
  };

  useEffect(() => {
    fetchAllClubRecords().then(() => {
      setFetchingPublishedRecords(false);
    });
  }, [fetchAllClubRecords]);

  useEffect(() => {
    fetchPendingClubRecords().then(() => {
      setFetchingPendingRecords(false);
    });
  }, [fetchPendingClubRecords]);

  const closeEditPopup = () => {
    setShowEditPopup(false);
  };

  const openEditPopup = useCallback((selectedRecord) => {
    setSelectedClubRecordResult(selectedRecord);
    setShowEditPopup(true);
  }, []);

  const closeClubRecordEditPopup = () => {
    setShowClubRecordEditPopup(false);
    setSelectedRecord(null);
  };

  const officialRows = useMemo(
    () => getOfficialRecordTableSortData(publishedRecords),
    [publishedRecords]
  );

  const pendingRows = useMemo(
    () =>
      getPendingRecordTableSortData(pendingRecords).map((record) => ({
        ...record,
        buttons: (
          <IconTextButton
            text="Review Record"
            icon={<EyeIcon />}
            onClick={() => openEditPopup(record)}
            textColor={redColor}
            borderColor="white"
            height="unset"
            iconPadding="5px"
            iconColor={redColor}
            iconPosition="right"
            style={{
              fontWeight: 'bold',
            }}
          />
        ),
      })),
    [pendingRecords, openEditPopup]
  );

  // Event dropdown holds only events that actually have records.
  const eventOptions = useMemo(() => eventsInListOrder(officialRows), [officialRows]);

  // The stored event may no longer have records (or the default 100m may
  // not exist for this club) — fall back to the first event with records.
  // All Events stays All Events.
  const effectiveEventType =
    filters.eventType === 'all' || eventOptions.includes(filters.eventType)
      ? filters.eventType
      : eventOptions[0] || '';

  const ageOptions = useMemo(() => groupsInOrder(officialRows), [officialRows]);

  // The Pending Records tab's choices, from the results waiting for review. A
  // choice whose results have all been reviewed acts as All.
  const pendingEventOptions = useMemo(() => eventsInListOrder(pendingRows), [pendingRows]);
  const pendingAgeOptions = useMemo(() => groupsInOrder(pendingRows), [pendingRows]);
  const pendingGenderOptions = useMemo(() => gendersInOrder(pendingRows), [pendingRows]);
  const pendingEventType = pendingEventOptions.includes(pendingFilters.eventType)
    ? pendingFilters.eventType
    : 'all';
  const pendingAgeGroup = pendingAgeOptions.includes(pendingFilters.ageGroup)
    ? pendingFilters.ageGroup
    : 'all';
  const pendingGender = pendingGenderOptions.includes(pendingFilters.gender)
    ? pendingFilters.gender
    : 'all';

  const displayedRows = useMemo(() => {
    const query = filters.searchText.trim().toLowerCase();
    const matchesAthlete = (record) =>
      (record.athleteName || '').toLowerCase().includes(query);

    if (optionValues.dataType === 'pending') {
      // Search spans all events, as on the Records tab; otherwise the
      // pending tab's own filters narrow the queue (All Events to start).
      return searchActive
        ? pendingRows.filter(matchesAthlete)
        : pendingRows.filter(
            (record) =>
              (pendingEventType === 'all' || record.eventType === pendingEventType) &&
              (pendingAgeGroup === 'all' ||
                String(record.ageGroup) === pendingAgeGroup) &&
              (pendingGender === 'all' || record.gender === pendingGender)
          );
    }

    // Search spans ALL events (athlete-centric lookup); otherwise the
    // dropdowns drive an event-centric browse.
    const rows = searchActive
      ? officialRows.filter(matchesAthlete)
      : officialRows.filter(
          (record) =>
            (effectiveEventType === 'all' || record.eventType === effectiveEventType) &&
            (filters.ageGroup === 'all' ||
              String(record.ageGroup) === filters.ageGroup) &&
            (filters.gender === 'all' || record.gender === filters.gender)
        );

    // Officials get the row payload the Actions cell renderer expects.
    return isOfficial ? rows.map((record) => ({ ...record, actions: record })) : rows;
  }, [
    optionValues.dataType,
    pendingRows,
    officialRows,
    searchActive,
    effectiveEventType,
    filters.searchText,
    filters.ageGroup,
    filters.gender,
    pendingEventType,
    pendingAgeGroup,
    pendingGender,
    isOfficial,
  ]);

  const isPublishedView = optionValues.dataType === 'published';
  const totalRows = isPublishedView ? officialRows.length : pendingRows.length;

  // Generate columns with action handlers for published records
  const publishedColumnsWithActions = getPublishColumns(
    handleEditRecord,
    handleDeleteRecord
  );

  return (
    <>
      <MetaTags title={`SplitFast | ${recordsTitle}`} />
      <AdminTablePageLayout
        loading={
          fetchingPublishedRecords ||
          fetchingPendingRecords ||
          updatingStatus ||
          isDeleting
        }
      >
        <AdminPageHeader showNumber={false} title={recordsTitle} />
        <div className={styles.adminPageOptionContainer}>
          <div className={styles.adminPageOptionSubContainer}>
            {isOfficial && (
              <AdminPageOption
                dataTypeOptions={dataTypeOptions}
                hasButton={false}
                handleOptionChange={handleOptionChange}
                optionValues={optionValues}
              />
            )}
            {isOfficial && clubRecordsPendingCount > 0 && (
              <div className={styles.badge}>{clubRecordsPendingCount} new</div>
            )}
          </div>

          {/* Add Record Button */}
          {isPublishedView && isOfficial && (
            <div className={styles.addButtonContainer}>
              <CustomNewButton
                label="+ Add Record"
                variant="primary"
                onClick={handleAddRecord}
                style={{ marginLeft: 'auto' }}
              />
            </div>
          )}

          {/* Update Pending Records, beside the Pending Records list */}
          {!isPublishedView && isAdmin && (
            <div className={styles.addButtonContainer}>
              <CustomNewButton
                label="Update Pending Records"
                variant="primary"
                onClick={handleUpdatePendingRecords}
                disabled={isUpdatingPending || !clubId}
                tooltip={`Check this season's results against the ${organisationWord} records now.`}
                style={{ marginLeft: 'auto' }}
              />
            </div>
          )}
        </div>

        {!isPublishedView && isAdmin && (
          <div className={styles.pendingNote} role="note">
            <InfoIcon className={styles.pendingNoteIcon} aria-hidden="true" />
            <span>
              Changes to results show here within an hour. Press{' '}
              <strong>Update Pending Records</strong> to bring this list up to date now.
            </span>
          </div>
        )}

        {/* Filter card — athlete search + event/age/gender browse dropdowns.
            While a search is active the dropdowns are disabled (search spans
            all events, so no dropdown setting is applied). The pending queue
            has its own dropdowns, with All Events, so it can show every event
            at once. */}
        <div className={`${filterStyles.filterCard} ${styles.recordsFilterCard}`}>
          <div
            className={`${filterStyles.filterGroup} ${filterStyles.filterGroupSearch}`}
          >
            <label className={filterStyles.filterLabel}>Search</label>
            <input
              className={filterStyles.filterInput}
              type="search"
              placeholder="Search athletes"
              value={filters.searchText}
              onChange={(e) => setFilter('searchText', e.target.value)}
            />
          </div>
          {isPublishedView && (
            <>
              <div
                className={`${filterStyles.filterGroup} ${searchActive ? styles.filterGroupDisabled : ''}`}
              >
                <label className={filterStyles.filterLabel}>Event</label>
                <select
                  className={filterStyles.filterSelect}
                  value={effectiveEventType}
                  disabled={searchActive}
                  onChange={(e) => setFilter('eventType', e.target.value)}
                >
                  <option value="all">All Events</option>
                  {eventOptions.map((event) => (
                    <option key={event} value={event}>
                      {event}
                    </option>
                  ))}
                </select>
              </div>
              <div
                className={`${filterStyles.filterGroup} ${searchActive ? styles.filterGroupDisabled : ''}`}
              >
                <label className={filterStyles.filterLabel}>{words.group}</label>
                <select
                  className={filterStyles.filterSelect}
                  value={filters.ageGroup}
                  disabled={searchActive}
                  onChange={(e) => setFilter('ageGroup', e.target.value)}
                >
                  <option value="all">All {words.values}</option>
                  {ageOptions.map((age) => (
                    <option key={age} value={age}>
                      {formatGroupName(groupingBasis, age)}
                    </option>
                  ))}
                </select>
              </div>
              <div
                className={`${filterStyles.filterGroup} ${searchActive ? styles.filterGroupDisabled : ''}`}
              >
                <label className={filterStyles.filterLabel}>Gender</label>
                <select
                  className={filterStyles.filterSelect}
                  value={filters.gender}
                  disabled={searchActive}
                  onChange={(e) => setFilter('gender', e.target.value)}
                >
                  <option value="all">All</option>
                  {clubFilterOptions.gender.values.map((gender) => (
                    <option key={gender} value={gender}>
                      {gender}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
          {!isPublishedView && (
            <>
              <div
                className={`${filterStyles.filterGroup} ${searchActive ? styles.filterGroupDisabled : ''}`}
              >
                <label className={filterStyles.filterLabel}>Event</label>
                <select
                  className={filterStyles.filterSelect}
                  value={pendingEventType}
                  disabled={searchActive}
                  onChange={(e) => setPendingFilter('eventType', e.target.value)}
                >
                  <option value="all">All Events</option>
                  {pendingEventOptions.map((event) => (
                    <option key={event} value={event}>
                      {event}
                    </option>
                  ))}
                </select>
              </div>
              <div
                className={`${filterStyles.filterGroup} ${searchActive ? styles.filterGroupDisabled : ''}`}
              >
                <label className={filterStyles.filterLabel}>{words.group}</label>
                <select
                  className={filterStyles.filterSelect}
                  value={pendingAgeGroup}
                  disabled={searchActive}
                  onChange={(e) => setPendingFilter('ageGroup', e.target.value)}
                >
                  <option value="all">All {words.values}</option>
                  {pendingAgeOptions.map((age) => (
                    <option key={age} value={age}>
                      {formatGroupName(groupingBasis, age)}
                    </option>
                  ))}
                </select>
              </div>
              <div
                className={`${filterStyles.filterGroup} ${searchActive ? styles.filterGroupDisabled : ''}`}
              >
                <label className={filterStyles.filterLabel}>Gender</label>
                <select
                  className={filterStyles.filterSelect}
                  value={pendingGender}
                  disabled={searchActive}
                  onChange={(e) => setPendingFilter('gender', e.target.value)}
                >
                  <option value="all">All</option>
                  {pendingGenderOptions.map((gender) => (
                    <option key={gender} value={gender}>
                      {gender}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
          {/* No row counts in browse mode — the event view is a deliberate
              subset, so an "of N total" denominator only confuses. The one
              count that earns its place: search mode, where it explains the
              greyed dropdowns and confirms the search spanned all events. */}
          {searchActive && isPublishedView && (
            <div className={filterStyles.filterResultCount}>
              Search matches <strong>{displayedRows.length}</strong>{' '}
              {displayedRows.length === 1 ? 'record' : 'records'} from all
              events
            </div>
          )}
        </div>

        {displayedRows.length > 0 ? (
          <AdminDataTable
            columns={
              isPublishedView
                ? isOfficial
                  ? publishedColumnsWithActions
                  : publishColumns
                : pendingColumns
            }
            data={displayedRows}
            isAddedFeatures={false}
            headStyle={
              isPublishedView
                ? isOfficial
                  ? {
                      gridTemplateColumns: '3fr minmax(140px, 3fr) 2fr 2fr 2fr 3fr 2fr 2fr', // Event, Track Surface (never narrower than its badge), Age Group, Gender, Result, Athlete, Date, Actions
                    }
                  : {
                      gridTemplateColumns: '3fr minmax(140px, 3fr) 2fr 2fr 2fr 3fr 2fr', // Event, Track Surface (never narrower than its badge), Age Group, Gender, Result, Athlete, Date
                    }
                : {
                    gridTemplateColumns: '3fr minmax(140px, 3fr) 2fr 2fr 2fr 3fr 2fr 3fr', // Event, Track Surface (never narrower than its badge), Age Group, Gender, Result, Athlete, Date (was 3fr: its share went to Track Surface), Buttons
                  }
            }
            rowStyle={
              isPublishedView
                ? isOfficial
                  ? {
                      gridTemplateColumns: '3fr minmax(140px, 3fr) 2fr 2fr 2fr 3fr 2fr 2fr', // Event, Track Surface (never narrower than its badge), Age Group, Gender, Result, Athlete, Date, Actions
                    }
                  : {
                      gridTemplateColumns: '3fr minmax(140px, 3fr) 2fr 2fr 2fr 3fr 2fr', // Event, Track Surface (never narrower than its badge), Age Group, Gender, Result, Athlete, Date
                    }
                : {
                    gridTemplateColumns: '3fr minmax(140px, 3fr) 2fr 2fr 2fr 3fr 2fr 3fr', // Event, Track Surface (never narrower than its badge), Age Group, Gender, Result, Athlete, Date (was 3fr: its share went to Track Surface), Buttons
                  }
            }
          />
        ) : (
          <div style={{ textAlign: 'center' }}>
            {isPublishedView
              ? totalRows === 0
                ? `There are no Official ${recordsTitle}`
                : searchActive
                  ? 'No records match your search'
                  : 'No records match the selected filters'
              : searchActive
                ? 'No pending records match your search'
                : `There are no Pending ${recordsTitle}`}
          </div>
        )}
      </AdminTablePageLayout>

      {/* Pending Record Review Popup */}
      <ClubRecordApprovalPopup
        clubID={selectedClub?.clubID}
        selectedClubRecordResult={selectedClubRecordResult}
        showPopup={showEditPopup}
        closePopup={closeEditPopup}
        changeStatus={changeStatusOfPendingClubRecordResult}
      />

      {/* Club Record Edit/Add Popup */}
      <ClubRecordEditPopup
        showPopup={showClubRecordEditPopup}
        closePopup={closeClubRecordEditPopup}
        mode={clubRecordEditMode}
        selectedRecord={selectedRecord}
        eventOptions={recordEventOptions.options}
        ageOptions={recordEventOptions.ageOptions}
        onSave={handleSaveClubRecord}
        onDelete={handleDeleteClubRecord}
      />

      {/* Delete Confirmation Dialog */}
      <CustomNewConfirm
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setRecordToDelete(null);
        }}
        title={`Delete ${organisationTitle} Record`}
        description={`Are you sure you want to delete this ${organisationWord} record?`}
        confirmButtonText="Delete Record"
        confirmButtonVariant="danger"
        onConfirm={confirmDeleteRecord}
        cancelButtonText="Cancel"
        showWarning={true}
        warningText="This action cannot be undone. The record will be permanently removed from the official records."
        showImpactSummary={recordToDelete ? true : false}
        impactItems={
          recordToDelete
            ? [
                {
                  label: 'Event',
                  value: String(recordToDelete.eventType || ''),
                },
                {
                  label: 'Athlete',
                  value: String(recordToDelete.athleteName || ''),
                },
                {
                  label: 'Result',
                  value: String(
                    formatRecordValueForDisplay(
                      recordToDelete.recordValue,
                      recordToDelete.eventType
                    ) ||
                      recordToDelete.result ||
                      ''
                  ),
                },
                {
                  label: words.group,
                  value: formatGroupName(groupingBasis, recordToDelete.ageGroup),
                },
              ]
            : []
        }
        isLoading={isDeleting}
      />
    </>
  );
};

export default ClubRecords;
